import { AlertTriangle, Phone } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { cn } from '@/lib/utils';
import type { EmergencyTrigger } from '../types';

interface EmergencyBannerProps {
  triggers: EmergencyTrigger[];
}

export function EmergencyBanner({ triggers }: EmergencyBannerProps) {
  const { t } = useTranslation('contractionTimer');
  if (triggers.length === 0) return null;

  // Show the most severe trigger first
  const criticalTriggers = triggers.filter((t) => t.severity === 'critical');
  const warningTriggers = triggers.filter((t) => t.severity === 'warning');

  const primaryTrigger = criticalTriggers[0] || warningTriggers[0];
  const isCritical = primaryTrigger.severity === 'critical';

  return (
    <div
      className={cn(
        'rounded-xl p-4 shadow-lg',
        isCritical
          ? 'bg-red-500'
          : 'bg-orange-500'
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
            isCritical ? 'bg-white/20' : 'bg-white/20'
          )}
        >
          <AlertTriangle className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-white font-bold">
            {isCritical ? t('emergencyBanner.critical') : t('emergencyBanner.warning')}
          </h3>
          <p className="text-white/90 text-sm mt-0.5">
            {primaryTrigger.message}
          </p>
          {triggers.length > 1 && (
            <p className="text-white/70 text-xs mt-1">
              {triggers.length === 2
                ? t('emergencyBanner.moreWarning', { count: triggers.length - 1 })
                : t('emergencyBanner.moreWarnings', { count: triggers.length - 1 })}
            </p>
          )}
        </div>
      </div>

      {isCritical && (
        <a
          href="tel:112"
          className="mt-3 w-full py-2.5 bg-white text-red-600 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-gray-100 transition-colors"
        >
          <Phone className="w-5 h-5" />
          {t('emergencyBanner.emergency')}
        </a>
      )}
    </div>
  );
}
